/**
 * The tide pools — each budgeted category filling to its mark, and a wider
 * pool at the side for everything together (DESIGN_SYSTEM §4.1).
 *
 * Each category pool is `<CssWater>`: a dozen graphics contexts in a row would
 * be absurd, and a filled box with a crest line is all a 2.4rem column needs.
 * The overall pool uses the same static fallback; Home needs no canvas.
 *
 * D-4: every pool is decoration under figures that say the same thing — the
 * percentage, the amount and the word "left" or "over". Over the mark, the pool
 * floods coral, the figures turn coral **and** say "over" (D-8).
 */

import { useRef } from "react";

import { type Cents, absCents, cents } from "@/domain/money";
import { CategoryGlyph } from "@/features/common/category_glyph";
import { cx } from "@/features/common/primitives";
import { poolFigure } from "@/features/horizon/tide_pools";
import type { CategoryStanding } from "@/hooks/use_data";
import { CssWater } from "@/water/css_water";

const PERCENT = 100;
/** Vertical travel that turns a press on the pools into "open the List". */
export const LIST_DRAG_PX = 48;

function percent(ratio: number): string {
  return `${String(Math.floor(ratio * PERCENT))}%`;
}

function leftOrOver(remaining: Cents, currency: string): { figure: string; word: "left" | "over" } {
  return remaining < 0
    ? { figure: poolFigure(absCents(remaining), currency), word: "over" }
    : { figure: poolFigure(remaining, currency), word: "left" };
}

export function Pools({
  pools,
  overall,
  currency,
  footer,
  onOpenList,
  onOpenCategory,
}: {
  pools: readonly CategoryStanding[];
  overall: { ratio: number; remaining: Cents; isOver: boolean };
  currency: string;
  rippleKey: number;
  footer: React.ReactNode;
  onOpenList: () => void;
  onOpenCategory: (id: string) => void;
}): React.JSX.Element {
  // A drag up anywhere on the pools opens the List, as the mock's handle
  // would; a tap still opens a pool. The flag swallows the click that ends a
  // drag, so lifting a finger over a pool does not also navigate.
  const drag = useRef<{ y: number; dragged: boolean } | null>(null);
  const all = leftOrOver(overall.remaining, currency);

  return (
    <section
      aria-labelledby="pools-heading"
      onPointerDown={(event) => {
        drag.current = { y: event.clientY, dragged: false };
      }}
      onPointerUp={(event) => {
        const start = drag.current;
        if (start && start.y - event.clientY > LIST_DRAG_PX) {
          start.dragged = true;
          onOpenList();
        }
      }}
      onClickCapture={(event) => {
        if (drag.current?.dragged) {
          event.preventDefault();
          event.stopPropagation();
        }
        drag.current = null;
      }}
      className="@container flex min-h-0 flex-1 flex-col px-phi-n2"
    >
      <div className="flex items-center justify-between gap-phi-n2 px-phi-n4 pb-phi-n2">
        <h2 id="pools-heading" className="min-w-0 text-phi-n1 font-semibold tracking-[0.16em] text-tx-2 uppercase">
          Each category, filling to its mark
        </h2>
        <button
          type="button"
          onClick={onOpenList}
          aria-haspopup="dialog"
          className="min-h-[44px] min-w-[44px] shrink-0 rounded-full border border-line px-phi-0 text-phi-n1 font-semibold text-tx"
        >
          List
        </button>
      </div>

      <div className="flex min-h-0 flex-1 gap-phi-n4">
        <ul data-swipe-claim className="flex min-w-0 flex-1 touch-pan-x gap-phi-n4 overflow-x-auto pb-phi-n4 [scrollbar-width:none] @3xl:grid @3xl:grid-cols-4 @3xl:gap-phi-1">
          {pools.map((standing) => {
            const remaining = standing.remaining ?? cents(0);
            const { figure, word } = leftOrOver(remaining, currency);
            return (
              <li key={standing.category.id} className="flex min-w-[44px] flex-1">
                <button
                  type="button"
                  onClick={() => onOpenCategory(standing.category.id)}
                  aria-label={`${standing.category.name}: ${percent(standing.ratio)} of its mark, ${figure} ${word}`}
                  className="flex w-full flex-col items-center gap-phi-n4"
                >
                  <span className="relative pb-phi-n4">
                    <CategoryGlyph category={standing.category} />
                    {standing.isOver ? (
                      <span aria-hidden="true" className="absolute -inset-x-phi-n4 bottom-0 h-[1px] rounded-full bg-over" />
                    ) : null}
                  </span>
                  <CssWater
                    level={Math.min(standing.ratio, 1)}
                    isOver={standing.isOver}
                    className="min-h-phi-6 w-full max-w-phi-4 flex-1 rounded-full bg-acc-soft"
                  />
                  <span className={cx("fig text-phi-n1 font-bold", standing.isOver && "text-over")}>
                    {percent(standing.ratio)}
                  </span>
                  <span
                    className={cx(
                      "fig text-center text-phi-n2 leading-normal",
                      standing.isOver ? "text-over" : "text-tx-2",
                    )}
                  >
                    {figure}
                    <br />
                    {word}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="flex w-phi-5 shrink-0 flex-col items-center gap-phi-n4 border-l border-line pb-phi-n4 pl-phi-n4">
          <span className="flex size-phi-3 items-center pb-phi-n4 text-phi-n2 font-semibold tracking-[0.16em] text-tx-2 uppercase">
            All
          </span>
          <CssWater
            level={Math.min(overall.ratio, 1)}
            isOver={overall.isOver}
            className="relative min-h-phi-6 w-full flex-1 overflow-hidden rounded-full bg-acc-soft"
          />
          <span className={cx("fig text-phi-n1 font-bold", overall.isOver && "text-over")}>
            {percent(overall.ratio)}
          </span>
          <span className={cx("fig text-center text-phi-n2 leading-normal", overall.isOver ? "text-over" : "text-tx-2")}>
            {all.figure}
            <br />
            {all.word}
          </span>
        </div>
      </div>

      <div className="px-phi-n4 py-phi-n2 text-center text-phi-n1 text-tx-2">{footer}</div>
    </section>
  );
}
